import React from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQuery } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Loader, ArrowLeft, BarChart, AlertTriangle, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getPlanDetails } from "@/services/subscriptionService";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Settings as SettingsIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { maskCPF, maskPhoneBR, isCPFFormatted, isPhoneFormatted } from "@/utils/masks";

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);
  const [lawyerInfo, setLawyerInfo] = React.useState({
    lawyer_name: '',
    oab_number: '',
    oab_state: '',
    cpf: '',
    whatsapp: '',
    include_lawyer_signature: false
  });
  const [isSaving, setIsSaving] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({ cpf: '', whatsapp: '' });

  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
      return data;
    },
  });

  // Carregar dados do advogado
  React.useEffect(() => {
    const loadLawyerInfo = async () => {
      if (!user?.id) return;
      
      const { data, error } = await supabase
        .from('pdf_settings')
        .select('lawyer_name, oab_number, oab_state, cpf, whatsapp, include_lawyer_signature')
        .eq('user_id', user.id)
        .single();
      
      if (data) {
        setLawyerInfo({
          lawyer_name: data.lawyer_name || '',
          oab_number: data.oab_number || '',
          oab_state: data.oab_state || '',
          cpf: data.cpf || '',
          whatsapp: data.whatsapp || '',
          include_lawyer_signature: data.include_lawyer_signature || false
        });
      }
    };
    
    loadLawyerInfo();
  }, [user?.id]);

  const handleCreateCheckout = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planType: 'basic' }
      });
      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Erro ao criar sessão de checkout:', error);
      toast({
        title: "Erro",
        description: "Não foi possível iniciar o processo de assinatura",
        variant: "destructive",
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Erro ao acessar portal do cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível acessar o portal de gerenciamento",
        variant: "destructive",
      });
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Erro ao acessar portal de cancelamento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível iniciar o processo de cancelamento",
        variant: "destructive",
      });
    }
    setShowCancelDialog(false);
  };

  const formatStorageLimit = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${Math.round(mb)} MB`;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  const handleViewPlans = () => {
    navigate('/planos');
  };

  // Handlers para aplicar máscaras
  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = maskCPF(e.target.value);
    setLawyerInfo(prev => ({ ...prev, cpf: maskedValue }));
    // Limpar erro ao digitar
    if (fieldErrors.cpf) {
      setFieldErrors(prev => ({ ...prev, cpf: '' }));
    }
  };

  const handleWhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = maskPhoneBR(e.target.value);
    setLawyerInfo(prev => ({ ...prev, whatsapp: maskedValue }));
    // Limpar erro ao digitar
    if (fieldErrors.whatsapp) {
      setFieldErrors(prev => ({ ...prev, whatsapp: '' }));
    }
  };

  const handleSaveLawyerInfo = async () => {
    if (!user?.id) return;
    
    // Validar formatos antes de salvar
    const errors = { cpf: '', whatsapp: '' };

    if (lawyerInfo.cpf && !isCPFFormatted(lawyerInfo.cpf)) {
      errors.cpf = 'CPF inválido. Use 000.000.000-00';
    }

    if (lawyerInfo.whatsapp && !isPhoneFormatted(lawyerInfo.whatsapp)) {
      errors.whatsapp = 'Telefone inválido. Use (00) 00000-0000';
    }

    if (errors.cpf || errors.whatsapp) {
      setFieldErrors(errors);
      return; // Não salvar se há erros
    }
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('pdf_settings')
        .upsert({
          user_id: user.id,
          lawyer_name: lawyerInfo.lawyer_name,
          oab_number: lawyerInfo.oab_number,
          oab_state: lawyerInfo.oab_state,
          cpf: lawyerInfo.cpf,
          whatsapp: lawyerInfo.whatsapp,
          include_lawyer_signature: lawyerInfo.include_lawyer_signature,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Identificação do advogado salva com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar as informações do advogado",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const planDetails = subscription?.plan ? getPlanDetails(subscription.plan) : getPlanDetails('free');
  const isPremium = subscription?.subscribed;

  return (
    <SidebarInset className="flex-1">
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <div className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            <h1 className="font-semibold">Configurações</h1>
          </div>
        </header>
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 bg-background overflow-auto">
            <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="flex items-center mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/painel')}
          className="mr-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar ao dashboard
        </Button>
        <h1 className="text-3xl font-bold">Configurações</h1>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Plano atual</CardTitle>
          <CardDescription>Gerencie sua assinatura</CardDescription>
        </CardHeader>
        <CardContent data-tour="current-plan">
          <div className="space-y-4">
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{planDetails.name}</p>
                  {subscription?.startDate && subscription?.endDate && (
                    <p className="text-sm text-muted-foreground">
                      Período atual: {formatDate(subscription.startDate)} até{' '}
                      {formatDate(subscription.endDate)}
                    </p>
                  )}
                  {subscription?.nextBillingDate && isPremium && (
                    <p className="text-sm text-muted-foreground">
                      Próxima renovação: {formatDate(subscription.nextBillingDate)}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="default"
                    onClick={handleViewPlans}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-tour="view-plans-button"
                  >
                    <BarChart className="mr-2 h-4 w-4" />
                    Ver planos
                  </Button>
                </div>
              </div>

              {isPremium && (
                <>
                  <Alert className="mt-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Assinatura é mensal (pré-paga). Exemplo: você paga no dia 10 e tem acesso até o dia 10 do mês seguinte.
                    </AlertDescription>
                  </Alert>

                  <div className="mt-4">
                    <Button 
                      variant="destructive" 
                      onClick={() => setShowCancelDialog(true)}
                      className="w-full sm:w-auto"
                    >
                      Cancelar assinatura
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      Ao cancelar, você manterá acesso aos recursos premium até {new Date(subscription.endDate).toLocaleDateString('pt-BR')}.
                      Após esta data, sua conta será convertida para o plano gratuito.
                    </p>
                  </div>
                </>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div className="border rounded p-4">
                  <p className="text-sm text-muted-foreground">Armazenamento</p>
                  <p className="font-medium">{formatStorageLimit(subscription?.storageLimit || 104857600)}</p>
                </div>
                <div className="border rounded p-4">
                  <p className="text-sm text-muted-foreground">Histórico</p>
                  <p className="font-medium">{subscription?.retentionDays || 1} {(subscription?.retentionDays || 1) > 1 ? 'dias' : 'dia'}</p>
                </div>
                <div className="border rounded p-4">
                  <p className="text-sm text-muted-foreground">Upload PDFs</p>
                  <p className={`font-medium ${(subscription?.pageLimit || 0) === 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {(subscription?.pageLimit || 0) === 0 ? 'Bloqueado' : `Até ${subscription?.pageLimit} páginas`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            🗂️ Identificação do Advogado
          </CardTitle>
          <CardDescription>
            Configure a identificação profissional que aparecerá no final dos documentos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lawyer-name">Nome Completo do Advogado</Label>
            <Input
              id="lawyer-name"
              placeholder="Ex: Dr. João Silva Santos"
              value={lawyerInfo.lawyer_name}
              onChange={(e) => setLawyerInfo(prev => ({ ...prev, lawyer_name: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="oab-number">Número da OAB</Label>
              <Input
                id="oab-number"
                placeholder="123456"
                value={lawyerInfo.oab_number}
                onChange={(e) => setLawyerInfo(prev => ({ ...prev, oab_number: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="oab-state">Estado da OAB</Label>
              <Select value={lawyerInfo.oab_state} onValueChange={(value) => setLawyerInfo(prev => ({ ...prev, oab_state: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AC">Acre</SelectItem>
                  <SelectItem value="AL">Alagoas</SelectItem>
                  <SelectItem value="AP">Amapá</SelectItem>
                  <SelectItem value="AM">Amazonas</SelectItem>
                  <SelectItem value="BA">Bahia</SelectItem>
                  <SelectItem value="CE">Ceará</SelectItem>
                  <SelectItem value="DF">Distrito Federal</SelectItem>
                  <SelectItem value="ES">Espírito Santo</SelectItem>
                  <SelectItem value="GO">Goiás</SelectItem>
                  <SelectItem value="MA">Maranhão</SelectItem>
                  <SelectItem value="MT">Mato Grosso</SelectItem>
                  <SelectItem value="MS">Mato Grosso do Sul</SelectItem>
                  <SelectItem value="MG">Minas Gerais</SelectItem>
                  <SelectItem value="PA">Pará</SelectItem>
                  <SelectItem value="PB">Paraíba</SelectItem>
                  <SelectItem value="PR">Paraná</SelectItem>
                  <SelectItem value="PE">Pernambuco</SelectItem>
                  <SelectItem value="PI">Piauí</SelectItem>
                  <SelectItem value="RJ">Rio de Janeiro</SelectItem>
                  <SelectItem value="RN">Rio Grande do Norte</SelectItem>
                  <SelectItem value="RS">Rio Grande do Sul</SelectItem>
                  <SelectItem value="RO">Rondônia</SelectItem>
                  <SelectItem value="RR">Roraima</SelectItem>
                  <SelectItem value="SC">Santa Catarina</SelectItem>
                  <SelectItem value="SP">São Paulo</SelectItem>
                  <SelectItem value="SE">Sergipe</SelectItem>
                  <SelectItem value="TO">Tocantins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cpf">CPF (para nota fiscal)</Label>
              <Input
                id="cpf"
                placeholder="000.000.000-00"
                maxLength={14}
                value={lawyerInfo.cpf}
                onChange={handleCPFChange}
              />
              {fieldErrors.cpf && (
                <p className="text-sm text-red-600">{fieldErrors.cpf}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="whatsapp">WhatsApp</Label>
              <Input
                id="whatsapp"
                placeholder="(00) 00000-0000"
                maxLength={15}
                value={lawyerInfo.whatsapp}
                onChange={handleWhatsAppChange}
              />
              {fieldErrors.whatsapp && (
                <p className="text-sm text-red-600">{fieldErrors.whatsapp}</p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="include-signature" 
              checked={lawyerInfo.include_lawyer_signature}
              onCheckedChange={(checked) => setLawyerInfo(prev => ({ ...prev, include_lawyer_signature: checked as boolean }))}
            />
            <Label htmlFor="include-signature">Incluir identificação no final do documento</Label>
          </div>

          <div className="pt-4">
            <Button 
              onClick={handleSaveLawyerInfo}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {isSaving ? <Loader className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isSaving ? 'Salvando...' : 'Salvar Identificação'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar assinatura</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Você está prestes a cancelar sua assinatura premium. Por favor, note que:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Você manterá acesso aos recursos premium até o fim do período pago ({new Date(subscription?.endDate).toLocaleDateString('pt-BR')})</li>
                <li>Após este período, sua conta será convertida para o plano gratuito</li>
                <li>Não há reembolso proporcional para cancelamentos</li>
                <li>Você pode reativar sua assinatura a qualquer momento</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirmar cancelamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
            </div>
          </div>
        </main>
      </SidebarInset>
  );
};

export default Settings;
